import type { PreferenceSchedule } from "@vTypes";
import type { NurseResponse } from "@/types/nurse-type";
import type { WantedShiftRequestResult } from "@/hooks/roster-wanted/useWantedShiftRequestsQuery";
import type { ShiftLookupMaps } from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";
import { resolveShiftFromWantedEntry } from "@/utils/wanted-shift-util";

function dayOfMonthInMonth(
  shiftDate: string,
  year: number,
  month: number
): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(shiftDate.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y !== year || mo !== month) return null;
  const dim = new Date(year, month, 0).getDate();
  if (d < 1 || d > dim) return null;
  return d;
}

function normalizeScheduleRow(
  schedule: (string | null)[] | string[] | undefined,
  daysInMonth: number
): (string | null)[] {
  const arr: (string | null)[] = schedule
    ? schedule.map((s) => (s == null || s === "" ? null : String(s)))
    : [];
  while (arr.length < daysInMonth) arr.push(null);
  if (arr.length > daysInMonth) arr.length = daysInMonth;
  return arr;
}

/**
 * GET `/wanted/{year}/{month}/shift-requests` 의 `results`를
 * `TableBody_wanted` / `usePreferenceSchedule` 와 동일한 `PreferenceSchedule[]` 형태로 맞춤.
 * - `base`: `/preferences/all` 기반 스케줄이 있으면 날짜별로 유지하고, API에 있는 날짜만 덮어씀.
 * - `maps`: 있으면 `shifts_table_id` → 현재 카탈로그 `shift_id` (`resolveShiftFromWantedEntry`).
 */
export function mapShiftRequestsToPreferenceSchedule(
  nurses: NurseResponse[],
  base: PreferenceSchedule[] | undefined,
  results: WantedShiftRequestResult[],
  year: number,
  month: number,
  maps: ShiftLookupMaps<ShiftCatalogShift> | undefined
): PreferenceSchedule[] {
  const daysInMonth = new Date(year, month, 0).getDate();
  const byNurse = new Map(results.map((r) => [r.nurse_id, r]));
  const baseByNurse = new Map((base ?? []).map((p) => [p.nurse_id, p]));

  return nurses.map((nurse) => {
    const nurseId = nurse.nurse_id;
    const fromApi = byNurse.get(nurseId);
    const baseRow = baseByNurse.get(nurseId);

    const schedule = normalizeScheduleRow(baseRow?.schedule, daysInMonth);
    const shiftMessages: Record<number, string> = {
      ...(baseRow?.shiftMessages ?? {}),
    };

    if (fromApi?.shifts?.length) {
      for (const row of fromApi.shifts) {
        const day = dayOfMonthInMonth(row.shift_date, year, month);
        if (day == null) continue;

        const displayShift = maps
          ? (resolveShiftFromWantedEntry(
              { shift_id: row.shift, shifts_table_id: row.shifts_table_id },
              maps
            )?.shift_id ?? row.shift)
          : row.shift;

        schedule[day - 1] = displayShift;
        if (row.comment != null && String(row.comment).trim() !== "") {
          shiftMessages[day] = String(row.comment).trim();
        }
      }
    }

    return {
      nurse_id: nurseId,
      schedule: schedule as string[],
      request: baseRow?.request,
      shiftMessages,
    };
  });
}
